import { useState, useEffect, useCallback } from 'react';
import { Users, Users2, MessageSquare, Calendar, AlertCircle } from 'lucide-react';
import {
  MetricCard,
  QuickActions,
  RecentUsers,
  RecentSquads,
  TrendChart,
  SystemStatus,
} from '../components/Dashboard';

// Types for dashboard data
interface DashboardStats {
  totalUsers: number;
  totalSquads: number;
  totalSessions: number;
  messagestoday: number;
  usersTrend: number;
  squadsTrend: number;
}

interface UserSummary {
  id: string;
  name: string;
  email: string;
  createdAt: string;
}

interface SquadSummary {
  id: string;
  name: string;
  memberCount: number;
  createdAt: string;
}

interface ChartDataPoint {
  date: string;
  value: number;
  label: string;
}

interface SystemService {
  name: string;
  status: 'healthy' | 'degraded' | 'down';
  latency: number;
}

interface DashboardApiResponse {
  totalUsers: number;
  totalSquads: number;
  totalSessions: number;
  messagestoday: number;
  usersTrend: number;
  squadsTrend: number;
  recentUsers: UserSummary[];
  recentSquads: SquadSummary[];
  userGrowthData: ChartDataPoint[];
  activityData: ChartDataPoint[];
  systemServices: SystemService[];
}

// API helper
async function fetchApi<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
}

export default function AdminDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalSquads: 0,
    totalSessions: 0,
    messagestoday: 0,
    usersTrend: 0,
    squadsTrend: 0,
  });
  const [userGrowthData, setUserGrowthData] = useState<ChartDataPoint[]>([]);
  const [activityData, setActivityData] = useState<ChartDataPoint[]>([]);
  const [recentUsers, setRecentUsers] = useState<UserSummary[]>([]);
  const [recentSquads, setRecentSquads] = useState<SquadSummary[]>([]);
  const [systemServices, setSystemServices] = useState<SystemService[]>([]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchApi<DashboardApiResponse>('/api/v1/admin/dashboard/stats');

      setStats({
        totalUsers: data.totalUsers,
        totalSquads: data.totalSquads,
        totalSessions: data.totalSessions,
        messagestoday: data.messagestoday,
        usersTrend: data.usersTrend,
        squadsTrend: data.squadsTrend,
      });

      setUserGrowthData(data.userGrowthData || []);
      setActivityData(data.activityData || []);
      setRecentUsers(data.recentUsers || []);
      setRecentSquads(data.recentSquads || []);
      setSystemServices(data.systemServices || []);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-1">Overview of your TARK platform</p>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
            <button
              onClick={loadDashboardData}
              className="ml-auto px-3 py-1 bg-red-900/50 hover:bg-red-900 rounded text-sm transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Metrics Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Total Users"
            value={stats.totalUsers}
            icon={Users}
            color="blue"
            loading={loading}
            trend={{ value: stats.usersTrend, label: 'vs last week' }}
          />
          <MetricCard
            title="Active Squads"
            value={stats.totalSquads}
            icon={Users2}
            color="green"
            loading={loading}
            trend={{ value: stats.squadsTrend, label: 'vs last week' }}
          />
          <MetricCard
            title="Sessions Today"
            value={stats.totalSessions}
            icon={Calendar}
            color="purple"
            loading={loading}
          />
          <MetricCard
            title="Messages Today"
            value={stats.messagestoday.toLocaleString()}
            icon={MessageSquare}
            color="yellow"
            loading={loading}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TrendChart
            title="User Growth (7 days)"
            data={userGrowthData}
            loading={loading}
            color="blue"
            valueLabel="New Users"
          />
          <TrendChart
            title="Activity (7 days)"
            data={activityData}
            loading={loading}
            color="green"
            valueLabel="Sessions"
          />
        </div>

        {/* Quick Actions & Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <QuickActions />
          </div>
          <SystemStatus services={systemServices} loading={loading} />
        </div>

        {/* Recent Activity Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RecentUsers users={recentUsers} loading={loading} />
          <RecentSquads squads={recentSquads} loading={loading} />
        </div>
    </div>
  );
}
